import React, { useState, useEffect, useCallback } from 'react';
import '../../styles/components.css';
import { fetchAuth } from '../../utils/api';

function InventoryView({ user, handleApiError, showNotification }) {
    const [productVariants, setProductVariants] = useState([]);
    const [masterItems, setMasterItems] = useState([]);
    const [loading, setLoading] = useState(true);

    const [isProductModalOpen, setIsProductModalOpen] = useState(false);
    const [isStockModalOpen, setIsStockModalOpen] = useState(false);

    const [isEditMode, setIsEditMode] = useState(false);
    const [editingVariant, setEditingVariant] = useState(null);

    const getStockStatus = (stock) => {
        if (stock <= 0) return { level: 'empty', label: 'Out of Stock' };
        if (stock <= 50) return { level: 'low', label: 'Low Stock' };
        return { level: 'healthy', label: 'In Stock' };
    };

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const [variantsRes, masterItemsRes] = await Promise.all([
                fetchAuth('/api/product-variants'),
                fetchAuth('/api/master-items')
            ]);
            if (variantsRes.ok) setProductVariants(await variantsRes.json());
            if (masterItemsRes.ok) setMasterItems(await masterItemsRes.json());
        } catch (error) {
            handleApiError(error);
        } finally {
            setLoading(false);
        }
    }, [handleApiError]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const openProductModal = (variant = null) => {
        if (variant) {
            setIsEditMode(true);
            setEditingVariant(variant);
        } else {
            setIsEditMode(false);
            setEditingVariant(null);
        }
        setIsProductModalOpen(true);
    };

    const handleSaveProduct = async (productData) => {
        const url = isEditMode ? `/api/products/${editingVariant.id}` : `/api/products`;
        const method = isEditMode ? 'PUT' : 'POST';
        try {
            const res = await fetchAuth(url, { method, body: JSON.stringify(productData) });
            if (!res.ok) throw new Error((await res.json()).message);
            showNotification(isEditMode ? 'Product updated successfully!' : 'Product added successfully!', 'success');
            setIsProductModalOpen(false);
            fetchData();
        } catch (err) { handleApiError(err); }
    };

    const handleDeleteVariant = async (variantId) => {
        if (!window.confirm("Are you sure you want to delete this product? All stock values will be removed.")) return;
        try {
            const res = await fetchAuth(`/api/product-variants/${variantId}`, { method: 'DELETE' });
            if (!res.ok) throw new Error((await res.json()).message);
            showNotification('Product deleted successfully.', 'success');
            fetchData();
        } catch (err) {
            handleApiError(err);
        }
    };

    const handleAddStock = async (stockData) => {
        if (!stockData.masterItemId || !stockData.quantity) {
            showNotification('Please select a product and enter a quantity.', 'error');
            return;
        }
        try {
            const res = await fetchAuth(`/api/master-items/${stockData.masterItemId}/add-stock`, {
                method: 'PUT',
                body: JSON.stringify({ quantityToAdd: stockData.quantity })
            });
            if (!res.ok) throw new Error((await res.json()).message);
            showNotification('Stock added successfully!', 'success');
            setIsStockModalOpen(false);
            fetchData();
        } catch (err) { handleApiError(err); }
    };

    const ProductModal = ({ onClose, onSave, isEdit, variant }) => {
        const [formData, setFormData] = useState({
            categoryName: isEdit ? variant.categoryName || '' : '',
            productName: isEdit && variant.masterItem ? variant.masterItem.name : '',
            quantity: isEdit && variant.masterItem ? variant.masterItem.stock : 0,
            size: isEdit && variant.masterItem ? variant.masterItem.size || '' : '',
            qualityCheck: isEdit && variant.masterItem ? variant.masterItem.qualityCheck : false,
            keywords: isEdit && variant.masterItem ? (variant.masterItem.keywords || []).join(', ') : '',
            variantName: isEdit ? variant.variantName : '',
            salePrice: isEdit ? variant.salePrice : '',
            purchasePrice: isEdit ? variant.purchasePrice || '' : '',
        });

        const handleChange = (e) => {
            const { name, value, type, checked } = e.target;
            setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
        };
        const handleSubmit = (e) => { e.preventDefault(); onSave(formData); };

        return (
            <div className="modal-overlay">
                <div className="modal-content wide-modal">
                    <h3>{isEdit ? 'Edit Product' : 'Add New Product'}</h3>
                    <form onSubmit={handleSubmit} className="modal-form-container">
                        <div className="modal-form-content">
                            <div className="form-grid-2">
                                <div className="input-group"><label>Product Name</label><input name="productName" value={formData.productName} onChange={handleChange} required /></div>
                                <div className="input-group"><label>Category</label><input name="categoryName" value={formData.categoryName} onChange={handleChange} placeholder="Optional" /></div>
                            </div>
                            <div className="form-grid-2">
                                <div className="input-group"><label>Initial Quantity</label><input name="quantity" type="number" value={formData.quantity} onChange={handleChange} /></div>
                                <div className="input-group"><label>Size</label><input name="size" value={formData.size} onChange={handleChange} placeholder="e.g., 25kg, 10-inch" /></div>
                            </div>
                            <div className="input-group"><label>Keywords for Search (comma-separated)</label><input name="keywords" value={formData.keywords} onChange={handleChange} placeholder="e.g., shower, steel, bathroom" /></div>
                            <hr className="form-divider" />
                            <h4>Variant & Pricing</h4>
                            <div className="form-grid-3" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                                <div className="input-group"><label>Variant Name</label><input name="variantName" value={formData.variantName} onChange={handleChange} placeholder="e.g., Steel Finish" /></div>
                                <div className="input-group"><label>Purchase Price/Cost (₹)</label><input name="purchasePrice" type="number" value={formData.purchasePrice} onChange={handleChange} /></div>
                                <div className="input-group"><label>Sale Price (₹)</label><input name="salePrice" type="number" value={formData.salePrice} onChange={handleChange} required /></div>
                            </div>
                            <div className="input-group checkbox-group">
                                <input id="qualityCheck" name="qualityCheck" type="checkbox" checked={formData.qualityCheck} onChange={handleChange} />
                                <label htmlFor="qualityCheck">Quality Check Passed</label>
                            </div>
                        </div>
                        <div className="modal-actions">
                            <button type="button" className="cancel-btn" onClick={onClose}>Cancel</button>
                            <button type="submit" className="confirm-btn">{isEdit ? 'Update Product' : 'Save Product'}</button>
                        </div>
                    </form>
                </div>
            </div>
        );
    };

    const AddStockModal = ({ onClose, onSave }) => {
        const [stockData, setStockData] = useState({ masterItemId: '', quantity: '' });
        const [searchTerm, setSearchTerm] = useState('');

        const handleChange = (e) => setStockData(prev => ({ ...prev, [e.target.name]: e.target.value }));
        const handleSubmit = (e) => { e.preventDefault(); onSave(stockData); };

        const filteredItems = masterItems.filter(item =>
            item.name && item.name.toLowerCase().includes(searchTerm.toLowerCase())
        );

        return (
            <div className="modal-overlay">
                <div className="modal-content">
                    <h3>Add New Stock</h3>
                    <form onSubmit={handleSubmit}>
                        <div className="input-group">
                            <label>Search Product</label>
                            <input
                                type="text"
                                placeholder="Type to search..."
                                className="customer-input"
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <div className="input-group">
                            <label>Select Product</label>
                            <select name="masterItemId" value={stockData.masterItemId} onChange={handleChange} required>
                                <option value="">-- Choose a product to restock --</option>
                                {filteredItems.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
                            </select>
                        </div>
                        <div className="input-group">
                            <label>Quantity to Add</label>
                            <input name="quantity" type="number" value={stockData.quantity} onChange={handleChange} placeholder="e.g., 50" required />
                        </div>
                        <div className="modal-actions">
                            <button type="button" className="cancel-btn" onClick={onClose}>Cancel</button>
                            <button type="submit" className="confirm-btn">Add Stock</button>
                        </div>
                    </form>
                </div>
            </div>
        );
    };

    if (loading) return <div>Loading...</div>;

    return (
        <div className="page-container">
            {isProductModalOpen && <ProductModal onClose={() => setIsProductModalOpen(false)} onSave={handleSaveProduct} isEdit={isEditMode} variant={editingVariant} />}
            {isStockModalOpen && <AddStockModal onClose={() => setIsStockModalOpen(false)} onSave={handleAddStock} />}

            <div className="page-header">
                <h2 className="page-title">Product & Inventory</h2>
                <div className="header-actions">
                    <button className="btn-secondary" onClick={() => setIsStockModalOpen(true)}>+ Add Stock</button>
                    <button className="btn-primary" onClick={() => openProductModal(null)}>+ Add Product</button>
                </div>
            </div>
            <div className="data-display">
                <h3>All Product Variants</h3>
                <div className="inventory-table-container">
                    <table className="inventory-table">
                        <thead>
                            <tr>
                                <th>Product Name</th>
                                <th>Variant</th>
                                <th>Stock</th>
                                <th>Sale Price</th>
                                <th>Purchase Price</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {productVariants.map(variant => (
                                <tr key={variant.id}>
                                    <td>{variant.masterItem ? variant.masterItem.name : ''}</td>
                                    <td>{variant.variantName}</td>
                                    <td>
                                        {(() => {
                                            const stock = variant.masterItem ? variant.masterItem.stock : 0;
                                            const status = getStockStatus(stock);
                                            return (
                                                <span className={`stock-badge stock-badge--${status.level}`}>
                                                    <span className={`stock-dot stock-dot--${status.level}`}></span>
                                                    {stock} {variant.masterItem?.unit || ''} — {status.label}
                                                </span>
                                            );
                                        })()}
                                    </td>
                                    <td>₹{variant.salePrice}</td>
                                    <td>{variant.purchasePrice ? `₹${variant.purchasePrice}` : '—'}</td>
                                    <td>
                                        <div className="table-actions">
                                            <button className="icon-btn edit" onClick={() => openProductModal(variant)}>
                                                <svg viewBox="0 0 24 24"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34a.9959.9959 0 00-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"></path></svg>
                                            </button>
                                            <button className="icon-btn delete" onClick={() => handleDeleteVariant(variant.id)}>
                                                <svg viewBox="0 0 24 24"><path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"></path></svg>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

export default InventoryView;
